import { Sidebar, SidebarContent, useSidebar } from '@/components/ui/sidebar';
import { FC, useEffect, useState } from 'react';
import { useFilters } from '@/hooks/FiltersContext';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { NbButton } from '@/components/Neobrut';
import { X } from 'lucide-react';
import { CAT_RACES, DOG_RACES, PET_COLORS } from '@/constants';

const cities = [
  {
    value: 'rosario',
    label: 'Rosario',
  },
  {
    value: 'Capitán Bermudez',
    label: 'Capitán Bermudez',
  },
  {
    value: 'Roldán',
    label: 'Roldán',
  },
  {
    value: 'Funes',
    label: 'Funes',
  },
  {
    value: 'Baigorria',
    label: 'Baigorria',
  },
  {
    value: 'Villa Gobernador Gálvez',
    label: 'Villa Gobernador Gálvez',
  },
  {
    value: 'Fray Luis Beltrán',
    label: 'Fray Luis Beltrán',
  },
  {
    value: 'Chabás',
    label: 'Chabás',
  },
  {
    value: 'Casilda',
    label: 'Casilda',
  },
  {
    value: 'Oliveros',
    label: 'Oliveros',
  },
  {
    value: 'Ybarlucea',
    label: 'Ybarlucea',
  },
  {
    value: 'Castelar',
    label: 'Castelar',
  },
  {
    value: 'Bell Ville',
    label: 'Bell Ville',
  },
];

export const AppSidebar: FC = () => {
  const { filters, updateFilters, resetFilters } = useFilters();
  const { setOpen, setOpenMobile } = useSidebar();

  const [localFilters, setLocalFilters] = useState({
    type: filters.type || undefined,
    race: filters.race || undefined,
    age: filters.age || undefined,
    gender: filters.gender || undefined,
    main_color: filters.main_color || undefined,
    sterilized: filters.sterilized || undefined,
    retained: filters.retained || undefined,
    city: filters.city || '',
  });

  useEffect(() => {
    setLocalFilters({
      type: filters.type || undefined,
      race: filters.race || undefined,
      age: filters.age || undefined,
      gender: filters.gender || undefined,
      main_color: filters.main_color || undefined,
      sterilized: filters.sterilized || undefined,
      retained: filters.retained || undefined,
      city: filters.city || '',
    });
  }, [filters]);

  const handleInputChange = (
    key: keyof typeof localFilters,
    value: string | boolean | PetType | PetStatus | PetAges | PetGender,
  ) => {
    setLocalFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const applyFilters = () => {
    updateFilters({
      ...localFilters,
      city: localFilters.city.trim() === '' ? undefined : localFilters.city,
    });
    setOpenMobile(false);
    setOpen(false);
  };

  const resetAllFilters = () => {
    resetFilters();
    setLocalFilters({
      type: undefined,
      race: undefined,
      age: undefined,
      gender: undefined,
      main_color: undefined,
      sterilized: undefined,
      retained: undefined,
      city: '',
    });
    setOpenMobile(false);
    setOpen(false);
  };

  return (
    <Sidebar className="fixed inset-0 z-50">
      <SidebarContent style={{ background: 'var(--nb-paper)' }}>
        <div className="p-4">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <div
                className="text-[10px] font-bold uppercase tracking-[0.08em] opacity-60"
                style={{ fontFamily: '"JetBrains Mono", monospace' }}
              >
                / filtros
              </div>
              <h2 className="font-display text-[26px] leading-none">Refiná la búsqueda</h2>
            </div>
            <button
              aria-label="Cerrar filtros"
              onClick={() => {
                setOpenMobile(false);
                setOpen(false);
              }}
              className="nb-btn nb-btn-sm fill-paper"
              style={{ padding: '4px 6px' }}
            >
              <X size={14} />
            </button>
          </div>
          <div className="nb-hairline mb-4" />

          <div className="mb-4">
            <label className="text-[11px] font-bold uppercase tracking-[0.08em]" style={{ fontFamily: '"JetBrains Mono", monospace' }}>Animal:</label>
            <Select
              onValueChange={(value) => handleInputChange('type', value)}
              value={localFilters.type}
            >
              <SelectTrigger className="h-10 w-full bg-white font-medium">
                <SelectValue placeholder="Seleccionar" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem key={'dog'} value="dog">
                  Perro
                </SelectItem>
                <SelectItem key={'cat'} value="cat">
                  Gato
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="mb-4">
            <label className="text-[11px] font-bold uppercase tracking-[0.08em]" style={{ fontFamily: '"JetBrains Mono", monospace' }}>Raza:</label>
            <Select
              onValueChange={(value) => handleInputChange('race', value)}
              value={localFilters.race}
            >
              <SelectTrigger className="h-10 w-full bg-white font-medium">
                <SelectValue placeholder="Seleccionar" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem
                  className="border-b-[1px] border-slate-400"
                  key={'Mestizo'}
                  value="mestizo"
                >
                  Mestizo
                </SelectItem>
                {localFilters.type === 'cat'
                  ? Object.entries(CAT_RACES).map(([key, value]) => (
                    <SelectItem key={key} value={value}>
                      {value}
                    </SelectItem>
                  ))
                  : localFilters.type === 'dog'
                    ? Object.entries(DOG_RACES).map(([key, value]) => (
                      <SelectItem key={key} value={value}>
                        {value}
                      </SelectItem>
                    ))
                    : null}
              </SelectContent>
            </Select>
          </div>

          <div className="mb-4">
            <label className="text-[11px] font-bold uppercase tracking-[0.08em]" style={{ fontFamily: '"JetBrains Mono", monospace' }}>Edad:</label>
            <Select
              onValueChange={(value) => handleInputChange('age', value)}
              value={localFilters.age}
            >
              <SelectTrigger className="h-10 w-full bg-white font-medium">
                <SelectValue placeholder="Seleccionar" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem key={'baby'} value="baby">
                  Cachorro
                </SelectItem>
                <SelectItem key={'young'} value="young">
                  Joven
                </SelectItem>
                <SelectItem key={'adult'} value="adult">
                  Adulto
                </SelectItem>
                <SelectItem key={'senior'} value="senior">
                  Senior
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="mb-4">
            <label className="text-[11px] font-bold uppercase tracking-[0.08em]" style={{ fontFamily: '"JetBrains Mono", monospace' }}>Género:</label>
            <Select
              onValueChange={(value) => handleInputChange('gender', value)}
              value={localFilters.gender}
            >
              <SelectTrigger className="h-10 w-full bg-white font-medium">
                <SelectValue placeholder="Seleccionar" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem key={'m'} value="m">
                  Macho
                </SelectItem>
                <SelectItem key={'f'} value="f">
                  Hembra
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="mb-4">
            <label className="text-[11px] font-bold uppercase tracking-[0.08em]" style={{ fontFamily: '"JetBrains Mono", monospace' }}>Color:</label>
            <Select
              onValueChange={(value) => handleInputChange('main_color', value)}
              value={localFilters.main_color}
            >
              <SelectTrigger className="h-10 w-full bg-white font-medium">
                <SelectValue placeholder="Seleccionar" />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(PET_COLORS).map(([key, label]) => (
                  <SelectItem key={key} value={key}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="mb-4">
            <label className="text-[11px] font-bold uppercase tracking-[0.08em]" style={{ fontFamily: '"JetBrains Mono", monospace' }}>Esterilizado:</label>
            <Select
              onValueChange={(value) => handleInputChange('sterilized', value === 'true')}
              value={localFilters.sterilized ? 'true' : 'false'}
            >
              <SelectTrigger className="h-10 w-full bg-white font-medium">
                <SelectValue placeholder="Seleccionar" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem key={'true'} value="true">
                  Sí
                </SelectItem>
                <SelectItem key={'false'} value="false">
                  No
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="mb-4">
            <label className="text-[11px] font-bold uppercase tracking-[0.08em]" style={{ fontFamily: '"JetBrains Mono", monospace' }}>Retenido:</label>
            <Select
              onValueChange={(value) => handleInputChange('retained', value === 'true')}
              value={localFilters.retained ? 'true' : 'false'}
            >
              <SelectTrigger className="h-10 w-full bg-white font-medium">
                <SelectValue placeholder="Seleccionar" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem key={'true'} value="true">
                  Sí
                </SelectItem>
                <SelectItem key={'false'} value="false">
                  No
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="mb-4">
            <label className="text-[11px] font-bold uppercase tracking-[0.08em]" style={{ fontFamily: '"JetBrains Mono", monospace' }}>Ciudad:</label>
            <Select
              onValueChange={(value) => handleInputChange('city', value)}
              value={localFilters.city}
            >
              <SelectTrigger className="h-10 w-full bg-white font-medium">
                <SelectValue placeholder="Seleccionar" />
              </SelectTrigger>
              <SelectContent>
                {cities.map((city) => (
                  <SelectItem key={city.value} value={city.value}>
                    {city.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-2 pt-2">
            <NbButton fill="urgent" onClick={resetAllFilters}>
              Limpiar
            </NbButton>
            <NbButton fill="ink" onClick={applyFilters}>
              Aplicar
            </NbButton>
          </div>
        </div>
      </SidebarContent>
    </Sidebar>
  );
};
